import { useState } from 'react';

const DOW_RU = ['вс','пн','вт','ср','чт','пт','сб'];

/**
 * weeks: { weekNum: { days: [{ day, dow, date }] } }
 * weekNums: [36, 37, 38, ...]
 *
 * Простой режим (без группировки):
 *   getDayMetrics(date) => { exits, sick, vacation, dayoff, overlaps }
 *
 * Режим с группировкой (для РМ — весь проект / по BU / по ТМ):
 *   groupLevels: [{ key: 'all', label: 'Весь проект' }, { key: 'bu', label: 'По BU' }, { key: 'tm', label: 'По ТМ' }]
 *   getGroups(levelKey) => [{ id, label }] — список групп для этого уровня ('all' → одна группа)
 *   getDayMetricsFor(date, levelKey, groupId) => { exits, sick, vacation, dayoff, overlaps }
 */
export default function DailyStatsPanel({
  weeks, weekNums, title = 'По дням',
  getDayMetrics,
  groupLevels, getGroups, getDayMetricsFor,
}) {
  const [open, setOpen] = useState(false);
  const hasGrouping = !!groupLevels?.length;
  const [levelKey, setLevelKey] = useState(hasGrouping ? groupLevels[0].key : null);

  const allDays = weekNums.flatMap(w => weeks[w]?.days || []);
  const groups = hasGrouping ? getGroups(levelKey) : [{ id: null, label: null }];

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600,
          color: 'var(--text)', cursor: 'pointer', width: '100%', justifyContent: 'space-between',
        }}
      >
        <span>📅 {title}</span>
        <span style={{ color: 'var(--text-muted)' }}>{open ? '▲ Свернуть' : '▼ Развернуть'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {hasGrouping && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {groupLevels.map(lvl => (
                <button
                  key={lvl.key}
                  onClick={() => setLevelKey(lvl.key)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: `1.5px solid ${levelKey === lvl.key ? 'var(--accent)' : 'var(--border)'}`,
                    background: levelKey === lvl.key ? 'var(--accent-light)' : 'white',
                    color: levelKey === lvl.key ? 'var(--accent)' : 'var(--text-muted)',
                  }}
                >
                  {lvl.label}
                </button>
              ))}
            </div>
          )}

          {groups.map(group => (
            <div key={group.id ?? 'single'} style={{ marginBottom: groups.length > 1 ? 12 : 0 }}>
              {group.label && (
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', padding: '6px 4px' }}>{group.label}</div>
              )}
              <div style={{ background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10 }}>День</th>
                        <th style={{ padding: '8px 6px', fontWeight: 700, color: 'var(--accent)', fontSize: 10 }}>Выходы</th>
                        <th style={{ padding: '8px 6px', fontWeight: 700, color: 'var(--red)', fontSize: 10 }}>БЛ</th>
                        <th style={{ padding: '8px 6px', fontWeight: 700, color: '#7D5A00', fontSize: 10 }}>ОТ</th>
                        <th style={{ padding: '8px 6px', fontWeight: 700, color: '#843C0C', fontSize: 10 }}>ДО</th>
                        <th style={{ padding: '8px 6px', fontWeight: 700, color: '#276221', fontSize: 10 }}>↔</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDays.map(({ day, dow, date }) => {
                        const m = hasGrouping ? getDayMetricsFor(date, levelKey, group.id) : getDayMetrics(date);
                        const isWe = dow === 0 || dow === 6;
                        return (
                          <tr key={date} style={{ borderBottom: '1px solid var(--border)', background: isWe ? '#FAFAFA' : 'white' }}>
                            <td style={{ padding: '6px 10px', color: isWe ? 'var(--text-muted)' : 'var(--text)', fontWeight: 500 }}>
                              {day} <span style={{ fontSize: 9, color: 'var(--text-light)' }}>{DOW_RU[dow]}</span>
                            </td>
                            <td style={{ padding: '6px', textAlign: 'center', fontWeight: 700 }}>{m.exits || ''}</td>
                            <td style={{ padding: '6px', textAlign: 'center', color: 'var(--red)' }}>{m.sick || ''}</td>
                            <td style={{ padding: '6px', textAlign: 'center', color: '#7D5A00' }}>{m.vacation || ''}</td>
                            <td style={{ padding: '6px', textAlign: 'center', color: '#843C0C' }}>{m.dayoff || ''}</td>
                            <td style={{ padding: '6px', textAlign: 'center', color: '#276221' }}>{m.overlaps || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
